import { TemplateValidationResult } from '../types';
import {
  TEMPLATE_HELPERS_CACHE_VERSION,
  getDefaultTemplateHelpersCacheScope,
  getTemplateHelpersFromSessionStorage,
} from '../../../../services/templateHelpersSessionStorage';
import { TemplateHelperMetadata } from '../../../../types';

const FALLBACK_BLOCK_HELPERS = new Set([
  'if',
  'nif',
  'eq',
  'neq',
  'each',
  'include',
  'ifIsModuloZero',
  'ifIsModuloZeroPlusOne',
]);
const FALLBACK_INLINE_HELPERS = new Set(['translate']);
const DYNAMIC_HELPERS_RELOAD_INTERVAL_MS = 5000;

interface OpenBlockToken {
  helperName: string;
}

interface SupportedHelperSets {
  blockHelpers: Set<string>;
  inlineHelpers: Set<string>;
}

let lastHelperSetResolveAt = 0;
let cachedHelperSets: SupportedHelperSets = {
  blockHelpers: FALLBACK_BLOCK_HELPERS,
  inlineHelpers: FALLBACK_INLINE_HELPERS,
};

function getSupportedHelperSets(): SupportedHelperSets {
  const now = Date.now();
  if (now - lastHelperSetResolveAt < DYNAMIC_HELPERS_RELOAD_INTERVAL_MS) {
    return cachedHelperSets;
  }

  lastHelperSetResolveAt = now;
  const helpersFromCache = getTemplateHelpersFromSessionStorage({
    scopeKey: getDefaultTemplateHelpersCacheScope(),
    version: TEMPLATE_HELPERS_CACHE_VERSION,
    allowStale: true,
  });

  const resolvedFromCache = resolveHelpersFromMetadata(helpersFromCache);
  if (!resolvedFromCache) {
    cachedHelperSets = {
      blockHelpers: FALLBACK_BLOCK_HELPERS,
      inlineHelpers: FALLBACK_INLINE_HELPERS,
    };
    return cachedHelperSets;
  }

  cachedHelperSets = resolvedFromCache;
  return cachedHelperSets;
}

function resolveHelpersFromMetadata(helpers: TemplateHelperMetadata[] | null): SupportedHelperSets | null {
  if (!helpers?.length) {
    return null;
  }

  const blockHelpers = new Set<string>();
  const inlineHelpers = new Set<string>();

  helpers.forEach((helper) => {
    const helperName = extractHelperName(helper);
    if (!helperName) {
      return;
    }

    if (helper.type === 'helper' && helper.endKey) {
      blockHelpers.add(helperName);
      return;
    }

    inlineHelpers.add(helperName);
  });

  if (blockHelpers.size === 0 && inlineHelpers.size === 0) {
    return null;
  }

  return { blockHelpers, inlineHelpers };
}

function extractHelperName(helper: TemplateHelperMetadata): string | null {
  const startKey = helper.startKey.trim();
  if (!startKey) {
    return null;
  }

  if (startKey.startsWith('#')) {
    const token = startKey.slice(1).trim();
    return token.split(/\s+/)[0] || null;
  }

  if (startKey.startsWith('{{') && startKey.endsWith('}}')) {
    const token = startKey.slice(2, -2).trim();
    return token.split(/\s+/)[0] || null;
  }

  return startKey.split(/\s+/)[0] || null;
}

/**
 * Validate email template HTML before saving.
 */
export function validateEmailTemplateHtml(html: string): TemplateValidationResult {
  const { blockHelpers, inlineHelpers } = getSupportedHelperSets();
  const errors: string[] = [];
  const warnings: string[] = [];

  const addUnique = (target: string[], message: string) => {
    if (!target.includes(message)) {
      target.push(message);
    }
  };

  // No <script> tags
  if (/<script[\s>]/i.test(html)) {
    errors.push('Il template contiene tag <script> non consentiti.');
  }

  // No duplicate <body>
  const bodyCount = (html.match(/<body[\s>]/gi) || []).length;
  if (bodyCount > 1) {
    errors.push(`Il template contiene ${bodyCount} tag <body>. Atteso 0 o 1.`);
  }

  // Handlebars block validation: stack-based to correctly validate nesting.
  const blockStack: OpenBlockToken[] = [];
  const tokenRegex = /\{\{([^{}]+)\}\}/g;
  let tokenMatch: RegExpExecArray | null;
  while ((tokenMatch = tokenRegex.exec(html)) !== null) {
    const tokenContent = tokenMatch[1].trim();

    if (!tokenContent) {
      continue;
    }

    if (tokenContent === 'else') {
      if (blockStack.length === 0) {
        addUnique(errors, 'Token Handlebars {{else}} trovato fuori da un blocco aperto.');
      }
      continue;
    }

    if (tokenContent.startsWith('#')) {
      const helperName = tokenContent.slice(1).split(/\s+/)[0];
      blockStack.push({ helperName });

      if (!blockHelpers.has(helperName)) {
        addUnique(warnings, `Helper a blocco non riconosciuto lato frontend: {{#${helperName}}}.`);
      }
      continue;
    }

    if (tokenContent.startsWith('/')) {
      const closingName = tokenContent.slice(1).trim();
      if (!closingName) {
        addUnique(errors, 'Token Handlebars di chiusura non valido: {{/}}.');
        continue;
      }

      if (blockStack.length === 0) {
        addUnique(errors, `Chiusura Handlebars senza apertura: {{/${closingName}}}.`);
        continue;
      }

      const top = blockStack[blockStack.length - 1];
      if (top.helperName === closingName) {
        blockStack.pop();
        continue;
      }

      const matchingIndex = findLastMatchingBlockIndex(blockStack, closingName);
      if (matchingIndex === -1) {
        addUnique(errors, `Chiusura Handlebars senza apertura corrispondente: {{/${closingName}}}.`);
      } else {
        addUnique(
          errors,
          `Annidamento Handlebars non valido: trovato {{/${closingName}}} ma il blocco corrente è {{#${top.helperName}}}.`,
        );
        // Recover parser state: drop nested invalid blocks and its matched opener.
        blockStack.splice(matchingIndex);
      }
      continue;
    }

    // Inline helper function style, e.g. {{translate key}}
    if (tokenContent.includes(' ')) {
      const inlineHelperName = tokenContent.split(/\s+/)[0];
      if (!inlineHelpers.has(inlineHelperName)) {
        addUnique(warnings, `Helper inline non riconosciuto lato frontend: {{${inlineHelperName} ...}}.`);
      }
    }
  }

  if (blockStack.length > 0) {
    for (const block of blockStack) {
      addUnique(errors, `Blocco Handlebars non chiuso: {{#${block.helperName}}}.`);
    }
  }

  // Check for broken Handlebars tokens ({{ without matching }})
  // Walk through the string looking for unmatched {{
  let i = 0;
  while (i < html.length - 1) {
    if (html[i] === '{' && html[i + 1] === '{') {
      const closeIdx = html.indexOf('}}', i + 2);
      if (closeIdx === -1) {
        errors.push('Placeholder Handlebars non chiuso trovato ({{ senza }}).');
        break;
      }
      // Check the token content doesn't span too far (likely broken)
      const tokenContent = html.slice(i, closeIdx + 2);
      if (tokenContent.length > 200) {
        errors.push('Placeholder Handlebars sospetto: contenuto troppo lungo, potrebbe essere corrotto.');
      }
      i = closeIdx + 2;
    } else {
      i++;
    }
  }

  // Check for leftover token wrapper spans (postprocess failure)
  if (/data-hbs-inline=/.test(html) || /data-hbs-block-open=/.test(html) || /data-hbs-block-close=/.test(html)) {
    errors.push('Token Handlebars wrapper non rimossi correttamente dal postprocess.');
  }

  return { valid: errors.length === 0, errors, warnings };
}

function findLastMatchingBlockIndex(stack: OpenBlockToken[], helperName: string): number {
  for (let i = stack.length - 1; i >= 0; i--) {
    if (stack[i].helperName === helperName) {
      return i;
    }
  }
  return -1;
}
